import "moment-timezone";
import moment from "moment";
import React, { useMemo, useState } from "react";
import "styled-components/macro"; // DO NOT REMOVE. Necessary for using the css={`...`} prop
import CategoryColumnFilter from "./categoryColumnFilter";
import { ReportActionButtonCellRenderer } from "../reporting-actions/button-cell-renderer";
import { ViewFlopptionsButtonCellRenderer } from "../view-flopptions-cell-renderer";
import { useGridContext } from "./my-ag-grid-react-context";
import _ from "lodash";
import { Button, Intent, Position, TextArea } from "@blueprintjs/core";
import Tooltip from "antd/es/tooltip";
import { StyledPopoverWrapper } from "../popover-wrapper";
import { Resizable } from "re-resizable";

const USE_BIG_HEADERS = false;

const nfInt = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const nfFloat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const FALSEY_VALUES = new Set([
  false,
  "false",
  "False",
  "No",
  "no",
  0,
  0.0,
  -0,
]);
const TRUTHY_VALUES = new Set([true, "true", "True", "Yes", "yes", 1, 1.0]);

function LinkedTextCellRenderer({ value: text, data, colDef }) {
  const link = data[colDef.__linkField];
  // noinspection JSConstructorReturnsPrimitive
  if (!link) {
    return <span>{text}</span>;
  } else {
    return (
      // eslint-disable-next-line react/jsx-no-target-blank
      <a href={inferMailtoLinks(link)} target="_blank">
        {text}
      </a>
    );
  }
}

const emailPatt = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
function validateEmail(email) {
  return emailPatt.test(email);
}

function inferMailtoLinks(url) {
  if (url && validateEmail(url)) {
    return `mailto:${url}`;
  } else {
    return url;
  }
}

function UrlCellRenderer({ value: url }) {
  // noinspection JSConstructorReturnsPrimitive
  return !url ? null : (
    // eslint-disable-next-line react/jsx-no-target-blank
    <a href={inferMailtoLinks(url)} target="_blank">
      Link
    </a>
  );
}

function timestampComparator(filterLocalValue, cellValue) {
  // Assume dates are stored as iso
  const cellDate = cellValue && new Date(cellValue);
  const filterLocalDate = filterLocalValue && new Date(filterLocalValue);

  if (!cellDate && !filterLocalDate) {
    return 0;
  } else if (!filterLocalDate) {
    return 1;
  } else if (!cellValue) {
    return -1;
  } else if (filterLocalDate < cellDate) {
    return 1;
  } else if (filterLocalDate > cellDate) {
    return -1;
  } else {
    return 0;
  }
}

export function dateVF({ value }) {
  let ret = null;
  if (value) {
    const dateString = new Date(value).toLocaleDateString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
    if (dateString !== "Invalid Date") {
      ret = dateString;
    }
  }
  return ret;
}

function timestampVF({ value, colDef }) {
  let ret = null;
  let fmt;
  if (value && _.isString(value)) {
    ret = moment(value);
    if (colDef.__timezone) {
      ret = ret.tz(colDef.__timezone);
      fmt = "ddd ll LT z";
    } else {
      ret = ret.utc(false);
      fmt = "ddd ll LT";
    }
    ret = ret.format(fmt);
  }
  return ret;
}

function booleanVF(params) {
  if (params.value === undefined || params.value === null) {
    return "";
  } else if (FALSEY_VALUES.has(params.value)) {
    return false;
  } else if (TRUTHY_VALUES.has(params.value)) {
    return true;
  } else {
    throw Error(
      `booleanFormatter cannot format params with params.value=${params.value}`
    );
  }
}

export function floatVF(params) {
  // noinspection JSUnresolvedFunction
  return nfFloat.format(params.value);
}

export function moneyVF(params) {
  // noinspection JSUnresolvedFunction
  return "$" + floatVF(params);
}

export function integerVF(params) {
  // noinspection JSUnresolvedFunction
  return nfInt.format(params.value);
}
//
// function TripNote() {
//   const [noteContents, setNoteContents] = useState("asdfasdfasdfsfasdf");
//   const theTripNote = (
//     <div
//       css={`
//         width: 100%;
//         padding: 12px;
//       `}
//     >
//       <Resizable
//         defaultSize={{
//           width: 320,
//           height: 200,
//         }}
//       >
//         <TextArea
//           css={`
//             width: 100%;
//             height: 100% !important;
//             overflow-x: scroll;
//             white-space: nowrap;
//             overflow-wrap: normal;
//             resize: none;
//           `}
//           // fill={true}
//           growVertically={false}
//           // large={true}
//           intent={Intent.PRIMARY}
//           onChange={(ev) => setNoteContents(ev.target.value)}
//           value={noteContents}
//         />
//       </Resizable>
//     </div>
//   );
//   return theTripNote;
// }
//
// function OpenTripNoteCellRenderer({ value, data, context }) {
//   const theTripNote = TripNote();
//   return (
//     <span>
//       <StyledPopoverWrapper
//         canEscapeKeyClose
//         content={theTripNote}
//         position={Position.BOTTOM_LEFT}
//       >
//         <Tooltip title="Trip Notes" placement="topLeft" mouseEnterDelay={1.5}>
//           <Button
//             data-testid={`${context.tableName}_open_trip_note_button__${data.id}`}
//             data-testclass={`${context.tableName}_open_trip_note_button`}
//             minimal
//             small
//             icon="annotation"
//             large={false}
//             intent="primary"
//           />
//         </Tooltip>
//       </StyledPopoverWrapper>
//       {value}
//     </span>
//   );
// }

// noinspection JSUnresolvedFunction
const DEFAULT_GRID_OPTIONS = {
  cellStyle: { "justify-content": "flex-end", "text-align": "right" },
  columnTypes: {
    dateColumn: {
      filter: "agDateColumnFilter",
      // filterParams: {
      //   comparator: timestampComparator,
      // },
      // comparator: timestampComparator,
      // @ts-ignore
      valueGetter: (params) => {
        const value = params.data[params.colDef.field];
        if (_.isDate(value)) {
          return value;
        } else if (_.isString(value) && value) {
          return new Date(value);
        } else {
          return null;
        }
      },
      valueFormatter: dateVF,
    },
    timestampColumn: {
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: timestampComparator,
      },
      comparator: timestampComparator,
      valueFormatter: timestampVF,
    },
    booleanColumn: {
      filter: "agNumberColumnFilter",
      valueFormatter: booleanVF,
    },
    integerColumn: {
      filter: "agNumberColumnFilter",
      valueFormatter: integerVF,
    },
    floatColumn: {
      filter: "agNumberColumnFilter",
      valueFormatter: floatVF,
    },
    moneyColumn: {
      filter: "agNumberColumnFilter",
      valueFormatter: moneyVF,
    },
    textColumn: {
      filter: "agTextColumnFilter",
    },
    urlColumn: {
      filter: "agTextColumnFilter",
      cellRenderer: "urlRenderer",
    },
    linkedTextColumn: {
      filter: "agTextColumnFilter",
      cellRenderer: "linkedTextRenderer",
    },
    categoryColumn: {
      filter: "categoryFilterer",
    },
  },
  frameworkComponents: {
    urlRenderer: UrlCellRenderer,
    linkedTextRenderer: LinkedTextCellRenderer,
    categoryFilterer: CategoryColumnFilter,
    reportActionButtonCellRenderer: ReportActionButtonCellRenderer,
    viewFlopptionsButtonCellRenderer: ViewFlopptionsButtonCellRenderer,
    // openTripNoteCellRenderer: OpenTripNoteCellRenderer,
  },
  defaultColDef: {
    resizable: true,
    sortable: true,
    sortingOrder: ["desc", "asc", null],
    filter: true,
    suppressAutoSizeColumns: !window.Cypress,
    filterParams: {
      applyButton: true,
      clearButton: true,
      debounceMs: 200,
    },
    maxWidth: USE_BIG_HEADERS ? 250 : undefined,
  },
  deltaRowDataMode: true,
  getRowNodeId: (data) => data.id,
  headerHeight: USE_BIG_HEADERS ? 48 : undefined,
  enableCellTextSelection: true,
  // all rows assigned CSS class 'my-green-class'
  rowClass: "ag-table--row--odd",

  // all odd rows assigned 'my-shaded-effect'
  getRowClass: (params) =>
    params.node.rowIndex % 2 === 0 ? "ag-table--row--even" : null,
  // suppressFieldDotNotation: true
};

function useGridOptions({ scrollbarWidth, ...onEvents }) {
  const eventListeners = Object.values(onEvents);
  const context = useGridContext();
  return useMemo(
    () => ({
      scrollbarWidth,
      ...onEvents,
      ...DEFAULT_GRID_OPTIONS,
      context,
    }),
    // eslint-disable-next-line
    [context, eventListeners, scrollbarWidth]
  );
}

export { useGridOptions };
